import React, { useRef, useState } from 'react';
import Wordmark from './Wordmark';
import './Landing.css';

const YT_URL_PATTERN = /(?:youtube\.com\/(?:watch|shorts)|youtu\.be\/)/i;

/**
 * Landing — the empty state. A clear white page with just the wordmark, an
 * upload prompt (whole page is a drop target), and a YouTube link field.
 */
const Landing = ({ onFileUpload, onYouTubeSample }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSampling, setIsSampling] = useState(false);
  const [error, setError] = useState(null);
  const [ytValue, setYtValue] = useState('');

  const loadFiles = (files) => {
    if (!files || files.length === 0) return;
    // Multi-file drops become the drum machine — for now take the first file.
    onFileUpload(files[0]);
  };

  const trySample = async (url) => {
    const trimmed = (url || '').trim();
    if (!trimmed) return;
    if (!YT_URL_PATTERN.test(trimmed)) {
      setError("that doesn't look like a youtube link");
      return;
    }
    setError(null);
    setIsSampling(true);
    try {
      await onYouTubeSample(trimmed);
    } catch (e) {
      setError(e.message || 'could not sample that video');
      setIsSampling(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (isSampling) return;
    loadFiles(e.dataTransfer.files);
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text) {
      setYtValue(text);
      e.preventDefault();
      trySample(text);
    }
  };

  return (
    <div
      className={`landing${isDragging ? ' dragging' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { if (e.target === e.currentTarget) setIsDragging(false); }}
      onDrop={handleDrop}
    >
      <h1 className="landing-title"><Wordmark /></h1>

      {isSampling ? (
        <div className="landing-status">sampling…</div>
      ) : (
        <>
          <button
            type="button"
            className="landing-drop"
            onClick={() => fileInputRef.current?.click()}
          >
            drop files to begin / click to upload files
          </button>

          <input
            className="landing-yt-input"
            type="text"
            value={ytValue}
            placeholder="or paste a youtube link here"
            spellCheck={false}
            onChange={(e) => { setYtValue(e.target.value); setError(null); }}
            onPaste={handlePaste}
            onKeyDown={(e) => { if (e.key === 'Enter') trySample(ytValue); }}
          />

          {error && <div className="landing-error">{error}</div>}
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        multiple
        onChange={(e) => loadFiles(e.target.files)}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default Landing;
